import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Package, X } from "lucide-react";
import { AddToCartButton } from "../cart/add-to-cart-button";
import { PlanAmountPicker } from "./plan-amount-picker";
import { ProductDescription } from "./product-description";
import { ProductPrice } from "./product-price";
import { isPlan, type Product } from "./types";

interface ProductDetailsDrawerProps {
  product: Product;
  onClose: () => void;
}

/** Right-hand panel with everything about one product, and a way to add it to the cart. */
export function ProductDetailsDrawer({ product, onClose }: ProductDetailsDrawerProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-hidden bg-ink/40 backdrop-blur-sm animate-in fade-in"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-details-title"
        className="absolute inset-y-0 right-0 flex w-screen max-w-md flex-col border-l border-line bg-elevated shadow-2xl animate-in slide-in-from-right duration-300"
      >
        <div className="flex items-center justify-between border-b border-line bg-soft/50 px-6 py-5">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-3">Product details</span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-ink-3 transition-colors hover:bg-soft hover:text-ink-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-brand">
            <Package className="h-7 w-7" />
          </div>

          <div>
            <h2 id="product-details-title" className="text-2xl font-bold leading-tight text-ink">{product.name}</h2>
            <ProductPrice product={product} size="lg" className="mt-3" />
            <p className="mt-1 text-xs text-ink-3">Exclusive of GST.</p>
          </div>

          <div className="border-t border-line pt-6">
            <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-ink-3">What's included</h3>
            <ProductDescription description={product.description} />
          </div>
        </div>

        {isPlan(product) ? (
          <div className="space-y-3 border-t border-line bg-soft/50 px-6 py-4">
            <PlanAmountPicker product={product} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 border-t border-line bg-soft/50 px-6 py-4">
            <button
              onClick={onClose}
              className="h-10 rounded-lg border border-line bg-elevated px-4 text-sm font-semibold text-ink hover:bg-soft"
            >
              Close
            </button>
            <AddToCartButton productId={product.id} productName={product.name} />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
